import { forwardRef, useState, useEffect, useMemo, useRef, useCallback } from 'react';

import TextField from '@mui/material/TextField';
import FormHelperText from '@mui/material/FormHelperText';
import { InputAdornment } from '@mui/material';

import StyledWrapper from './style';
import { Registry } from '../../FormBuilderLibrary';
import { stringToHTML } from '../utils';
import { getElementIcon } from '../utils';
import Tooltip from '@mui/material/Tooltip';

const MAX_PERCENTAGE_DIGITS = 12;

const coerceToString = (value) => {
	if (value === null || value === undefined) {
		return '';
	}
	if (typeof value === 'string') {
		return value;
	}
	if (typeof value === 'number' && Number.isFinite(value)) {
		return value.toString();
	}
	return '';
};

const trimDecimalZeros = (value) =>
	coerceToString(value)
		.replace(/(\.\d*?[1-9])0+$/, '$1')
		.replace(/\.0+$/, '');

const sanitizeCurrencyValue = (value) => {
	const stringValue = coerceToString(value).trim();
	if (stringValue === '') {
		return '';
	}
	let cleaned = stringValue.replace(/[^0-9.-]/g, '');
	if (cleaned === '' || cleaned === '-' || cleaned === '.' || cleaned === '-.') {
		return '';
	}
	if (cleaned.startsWith('-')) {
		cleaned = `-${cleaned.slice(1).replace(/-/g, '')}`;
	} else {
		cleaned = cleaned.replace(/-/g, '');
	}
	const parts = cleaned.split('.');
	if (parts.length > 2) {
		const integerPart = parts.shift();
		const decimalPart = parts.join('');
		cleaned = `${integerPart}.${decimalPart}`;
	}
	return cleaned;
};

const sanitizePercentageInput = (value) => {
	const stringValue = coerceToString(value);
	if (stringValue === '') {
		return '';
	}
	let sanitized = stringValue.replace(/[^0-9.]/g, '');
	const parts = sanitized.split('.');
	if (parts.length > 2) {
		sanitized = `${parts.shift()}.${parts.join('')}`;
	}
	if (sanitized.startsWith('.')) {
		sanitized = `0${sanitized}`;
	}
	if (sanitized.length > MAX_PERCENTAGE_DIGITS) {
		sanitized = sanitized.slice(0, MAX_PERCENTAGE_DIGITS);
	}
	return sanitized;
};

const normalizePercentageDecimal = (value) => {
	const sanitized = sanitizePercentageInput(value);
	if (sanitized === '') {
		return '';
	}
	const numericValue = Number(sanitized);
	if (Number.isNaN(numericValue)) {
		return '';
	}
	return trimDecimalZeros(numericValue.toString());
};

const formatPercentageFromDecimal = (value) => {
	const normalized = normalizePercentageDecimal(value);
	if (normalized === '') {
		return '';
	}
	const numeric = Number(normalized);
	if (Number.isNaN(numeric)) {
		return '';
	}
	const percentValue = numeric * 100;
	return trimDecimalZeros(percentValue.toFixed(6));
};

const MUINumberInput = forwardRef((props, ref) => {
	const { name, defaultValue, disabled, data, isFormBuilder } = props;

	const [value, setValue] = useState(defaultValue);
	const [refresh, setRefresh] = useState(defaultValue);
	const [hasError, setHasError] = useState(props?.data?.error || false);
	const isCurrencyField = props?.data?.description === 'currency';
	const isPercentageField = props?.data?.description === 'percentage';
	const [percentageValue, setPercentageValue] = useState(() =>
		isPercentageField ? normalizePercentageDecimal(defaultValue) : ''
	);
	const [percentageDraftValue, setPercentageDraftValue] = useState(() =>
		isPercentageField ? normalizePercentageDecimal(defaultValue) : ''
	);
	const lastNormalizedDefaultRef = useRef(
		isPercentageField ? normalizePercentageDecimal(defaultValue) : ''
	);
	const [isPercentageFocused, setIsPercentageFocused] = useState(false);
	const mergedInputRef = useCallback(
		(node) => {
			if (typeof ref === 'function') {
				ref(node);
			} else if (ref) {
				ref.current = node;
			}
		},
		[ref]
	);
	const effectivePercentageValue = isPercentageField
		? isPercentageFocused
			? normalizePercentageDecimal(percentageDraftValue)
			: percentageValue
		: '';
	const percentageDatasetValue = isPercentageField ? effectivePercentageValue : '';
	const displayedPercentageValue = isPercentageField
		? isPercentageFocused
			? percentageDraftValue
			: formatPercentageFromDecimal(percentageValue)
		: undefined;
	const resolvedCurrencySource = isCurrencyField ? (disabled ? defaultValue : value) : '';
	const currencyDatasetValue = isCurrencyField ? sanitizeCurrencyValue(resolvedCurrencySource) : '';

	function formatChangeEvent(number) {
		let value = number;
		value = value.replace(/[^0-9.]/g, '');
		if (value) {
			if (value[0] == 0 || value[0] == '.') {
				value = '';
			}
			if (value.includes('.')) {
				value = value.slice(0, 15);
				value = value.split('.');
				let numberPart = value.shift();
				let fractionalPart = value.join('');
				if (fractionalPart.length > 2) {
					fractionalPart = fractionalPart.slice(0, 2);
				}
				value = numberPart + '.' + fractionalPart;
				value = value.replace(/(?=(\d{3})+(\D))\B/g, ',');
			} else {
				value = value.slice(0, 12);
			}
		}
		return value;
	}

	function formatBlurEvent(number, isCurrency) {
		if (number && isCurrency) {
			if (number.includes('.')) {
				const parts = number.split('.');
				if (parts[1].length == 1) {
					number = number + '0';
				} else if (parts[1].length == 0) {
					number = number + '00';
				}
			} else {
				number = number + '.00';
			}
			number = number.replace(/(?=(\d{3})+(\D))\B/g, ',');
		}

		return number;
	}

	const handleCurrencyChange = (event) => {
		const number = event.target.value;
		setValue(formatChangeEvent(number));
		setHasError(false);
	};

	const handleCurrencyBlur = (event) => {
		let number = event.target.value;
		setValue(formatBlurEvent(number, true));
	};

	const handleGenericNumberChange = () => {
		setHasError(false);
	};

	const handlePercentageChange = (event) => {
		const sanitizedValue = sanitizePercentageInput(event.target.value);
		setPercentageDraftValue(sanitizedValue);
		setHasError(false);
	};

	const handlePercentageFocus = () => {
		setIsPercentageFocused(true);
		setPercentageDraftValue(percentageValue || '');
	};

	const handlePercentageBlur = () => {
		const normalizedValue = normalizePercentageDecimal(percentageDraftValue);
		setPercentageValue(normalizedValue);
		setPercentageDraftValue(normalizedValue);
		setIsPercentageFocused(false);
	};

	useEffect(() => {
		setHasError(props?.data?.error || false);
	}, [props?.data?.error]);

	useEffect(() => {
		if (!isPercentageField) {
			if (percentageValue !== '') {
				setPercentageValue('');
			}
			if (percentageDraftValue !== '') {
				setPercentageDraftValue('');
			}
			if (isPercentageFocused) {
				setIsPercentageFocused(false);
			}
			lastNormalizedDefaultRef.current = '';
			return;
		}

		const normalizedDefault = normalizePercentageDecimal(defaultValue);
		if (lastNormalizedDefaultRef.current !== normalizedDefault) {
			lastNormalizedDefaultRef.current = normalizedDefault;
			setPercentageValue(normalizedDefault);
			if (!isPercentageFocused) {
				setPercentageDraftValue(normalizedDefault);
			}
		}
	}, [defaultValue, isPercentageField, isPercentageFocused, percentageDraftValue, percentageValue]);

	const parsedLabel = useMemo(() => stringToHTML(props?.data?.label), [props?.data?.label]);
	const parsedHelperText = useMemo(
		() => stringToHTML(props?.data?.help_message),
		[props?.data?.help_message]
	);

	const styleForNotFormBuilder = !isFormBuilder
		? { width: '100%', marginTop: '16px' }
		: { width: '100%' };
	const [isOverflowing, setIsOverflowing] = useState(false);
	const labelRef = useRef(null);

	useEffect(() => {
		setRefresh(!refresh);
	}, [props]);

	useEffect(() => {
		// ARM-397. The form elements initially take up the entire width of the screen and then are resized to fit into the proper row size. This setTimeout adds a delay so we do this calculation after the form elements are set to their proper row width. Without this the scroll width and clientWidth will be the same on screens with a width of 2000px and above
		setTimeout(() => {
			if (labelRef.current) {
				setIsOverflowing(labelRef.current.scrollWidth > labelRef.current.clientWidth);
			}
		}, 100);
	});

	const percentageAdornmentNode = isPercentageField ? (
		<InputAdornment position="end">%</InputAdornment>
	) : null;
	const showPercentageAdornment = isPercentageField && !isPercentageFocused;
	const currencyAdornment = isCurrencyField ? (
		<InputAdornment position="start">$</InputAdornment>
	) : null;
	const readonlyInputProps = (() => {
		if (!currencyAdornment && !percentageAdornmentNode && !isPercentageField) {
			return undefined;
		}
		const baseProps = {
			...(currencyAdornment ? { startAdornment: currencyAdornment } : {}),
			...(percentageAdornmentNode ? { endAdornment: percentageAdornmentNode } : {})
		};
		const inputProps = { ...baseProps.inputProps };
		if (isCurrencyField) {
			inputProps['aria-label'] = props?.data?.label;
			if (currencyDatasetValue) {
				inputProps['data-rawvalue'] = currencyDatasetValue;
			}
			inputProps['data-fieldtype'] = 'currency';
		}
		if (isPercentageField) {
			inputProps['aria-label'] = props?.data?.label;
			if (percentageDatasetValue) {
				inputProps['data-rawvalue'] = percentageDatasetValue;
			}
			inputProps['data-fieldtype'] = 'percentage';
		}
		if (Object.keys(inputProps).length > 0) {
			baseProps.inputProps = inputProps;
		}
		return Object.keys(baseProps).length ? baseProps : undefined;
	})();
	return (
		<>
			{!disabled ? (
				isCurrencyField ? (
					<StyledWrapper.FormControlNoBorder sx={{ width: '100%' }} variant="outlined">
						<Tooltip
							arrow
							placement="top"
							title={isOverflowing ? `${props?.data?.label}` : ''}
							enterTouchDelay={0}
						>
							<StyledWrapper.CustomLabel>
								<StyledWrapper.LabelContents ref={labelRef}>
									{isFormBuilder && getElementIcon(props.data?.key)}
									{parsedLabel?.[0] ? parsedLabel : props?.data?.label}
									{isFormBuilder && (
										<span>
											{' '}
											{props.data?.unique_identifier
												? `(ff-${props.data?.unique_identifier})`
												: `(tmp-${props.data?.temp_order})`}
										</span>
									)}
								</StyledWrapper.LabelContents>
								{props?.data?.required && (
									<StyledWrapper.AsteriskSpan> *</StyledWrapper.AsteriskSpan>
								)}
							</StyledWrapper.CustomLabel>
						</Tooltip>
						<TextField
							error={hasError}
							required={props?.data?.required}
							id="outlined-error-helper-text"
							name={name}
							inputRef={mergedInputRef}
							disabled={disabled}
							value={value}
							type="string"
							onBlur={handleCurrencyBlur}
							onChange={handleCurrencyChange}
							style={styleForNotFormBuilder}
							InputProps={{
								inputComponent: 'input',
								inputProps: {
									maxLength: 18,
									inputMode: 'decimal',
									'aria-label': props?.data?.label,
									'data-rawvalue': currencyDatasetValue || undefined,
									'data-fieldtype': 'currency'
								},
								...(currencyAdornment ? { startAdornment: currencyAdornment } : {})
							}}
						/>
						<FormHelperText error={hasError}>
							{props.data.error?.error ? `${props.data.label} ${props.data.error?.error}` : ''}
						</FormHelperText>
						{parsedHelperText && <FormHelperText>{parsedHelperText}</FormHelperText>}
					</StyledWrapper.FormControlNoBorder>
				) : (
					<StyledWrapper.FormControlNoBorder sx={{ width: '100%' }} variant="outlined">
						<Tooltip
							arrow
							placement="top"
							title={isOverflowing ? `${props?.data?.label}` : ''}
							enterTouchDelay={0}
						>
							<StyledWrapper.CustomLabel>
								<StyledWrapper.LabelContents ref={labelRef}>
									{isFormBuilder && getElementIcon(props.data?.key)}
									{parsedLabel?.[0] ? parsedLabel : props?.data?.label}
									{isFormBuilder && (
										<span>
											{' '}
											{props.data?.unique_identifier
												? `(ff-${props.data?.unique_identifier})`
												: `(tmp-${props.data?.temp_order})`}
										</span>
									)}
								</StyledWrapper.LabelContents>
								{props?.data?.required && (
									<StyledWrapper.AsteriskSpan> *</StyledWrapper.AsteriskSpan>
								)}
							</StyledWrapper.CustomLabel>
						</Tooltip>
						<TextField
							error={hasError}
							required={props?.data?.required}
							id="outlined-error-helper-text"
							name={name}
							inputRef={mergedInputRef}
							disabled={disabled}
							{...(isPercentageField ? { value: displayedPercentageValue } : { defaultValue })}
							onChange={isPercentageField ? handlePercentageChange : handleGenericNumberChange}
							onFocus={isPercentageField ? handlePercentageFocus : undefined}
							onBlur={isPercentageField ? handlePercentageBlur : undefined}
							InputLabelProps={{ shrink: isFormBuilder || undefined }}
							style={styleForNotFormBuilder}
							type={isPercentageField ? 'text' : 'number'}
							InputProps={
								isPercentageField
									? {
											...(showPercentageAdornment && percentageAdornmentNode
												? { endAdornment: percentageAdornmentNode }
												: {}),
											inputProps: {
												'aria-label': props?.data?.label,
												'data-rawvalue': percentageDatasetValue || undefined,
												'data-fieldtype': 'percentage',
												inputMode: 'decimal',
												pattern: '[0-9.]*',
												maxLength: MAX_PERCENTAGE_DIGITS
											}
									  }
									: percentageAdornmentNode
									? { endAdornment: percentageAdornmentNode }
									: undefined
							}
							onWheel={isPercentageField ? undefined : (e) => e.target.blur()}
						/>
						<FormHelperText error={hasError}>
							{props.data.error?.error ? `${props.data.label} ${props.data.error?.error}` : ''}
						</FormHelperText>
						{parsedHelperText && <FormHelperText>{parsedHelperText}</FormHelperText>}
					</StyledWrapper.FormControlNoBorder>
				)
			) : (
				<StyledWrapper.FormControlNoBorder sx={{ width: '100%' }} variant="outlined">
					<Tooltip
						arrow
						placement="top"
						title={isOverflowing ? `${props?.data?.label}` : ''}
						enterTouchDelay={0}
					>
						<StyledWrapper.CustomLabel
							value={defaultValue}
							disabled={disabled}
							//Having required on customLabel will have double asterisks show up
							htmlFor="outlined-adornment-password"
						>
							<StyledWrapper.LabelContents ref={labelRef}>
								{isFormBuilder && getElementIcon(props.data?.key)}
								{parsedLabel?.[0] ? parsedLabel : props?.data?.label}
								{isFormBuilder && (
									<span>
										{' '}
										{props.data?.unique_identifier
											? `(ff-${props.data?.unique_identifier})`
											: `(tmp-${props.data?.temp_order})`}
									</span>
								)}
							</StyledWrapper.LabelContents>
							{props?.data?.required && <StyledWrapper.AsteriskSpan> *</StyledWrapper.AsteriskSpan>}
						</StyledWrapper.CustomLabel>
					</Tooltip>
					<StyledWrapper.OutlinedInputReadOnly
						id="outlined-adornment-password"
						type={'text'}
						disabled={disabled}
						style={{ width: '100%', border: '0px', marginTop: '16px' }}
						inputComponent={() => {
							return (
								<StyledWrapper.RemoveBorderContainer>
									<TextField
										error={hasError}
										required={props?.data?.required}
										id="outlined-error-helper-text"
										name={name}
										inputRef={mergedInputRef}
										disabled={disabled}
										defaultValue={
											isPercentageField
												? formatPercentageFromDecimal(defaultValue)
												: formatBlurEvent(defaultValue, isCurrencyField)
										}
										type="string"
										style={{ width: '100%' }}
										InputProps={readonlyInputProps}
									/>
									<StyledWrapper.PlaceHolderText>
										{data?.min_value &&
											data?.max_value &&
											`(${data?.min_value}-${data?.max_value})`}
									</StyledWrapper.PlaceHolderText>
								</StyledWrapper.RemoveBorderContainer>
							);
						}}
						value={defaultValue || ''}
					/>

					<FormHelperText error={hasError}>
						{props.data.error?.error ? `${props.data.label} ${props.data.error?.error}` : ''}
					</FormHelperText>
					{parsedHelperText && <FormHelperText>{parsedHelperText}</FormHelperText>}
				</StyledWrapper.FormControlNoBorder>
			)}
		</>
	);
});

MUINumberInput.displayName = 'MUI_NumberInput';
Registry.register('MUI_NumberInput', MUINumberInput);

export default MUINumberInput;
